import React, { useState, useEffect } from 'react';
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { Input, Pagination } from "antd";
import { useTranslation } from 'react-i18next';
import axiosInstance from "../../axios/axiosInstance";
import {
    ActionButton,
    DataTable,
    PageCard,
    PageShell,
    PageToolbar,
} from "../../components/ui";

export default function Mkb10() {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState('');
    const [records, setRecords] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        fetchRecords();
    }, []);

    const fetchRecords = async (page = 1) => {
        try {
            const response = await axiosInstance.get(`/mkb10?page=${page}`);
            setRecords(response.data.data);
            setCurrentPage(response.data.meta.current_page);
            setTotalPages(response.data.meta.last_page);
        } catch (error) {
            console.error("Ошибка при получении списка МКБ10:", error);
        }
    };

    const handleSearch = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get(`/mkb10?search=${searchQuery}`);
            setRecords(response.data.data);
            setCurrentPage(1); // Reset to first page after search
            setTotalPages(response.data.meta.last_page);
        } catch (error) {
            console.error("МКБ10 номларини қидиришда хатолик:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePageChange = (page) => {
        fetchRecords(page);
    };

    const shortenInfo = (text) => (text ? `${text.split(' ').slice(0, 7).join(' ')} ...` : '');

    const columns = [
        {
            title: t('common.code'),
            dataIndex: 'code',
            key: 'code',
            width: 120,
            render: (code) => (
                <span className="text-sm font-medium text-[#1D2C7B] tabular-nums whitespace-nowrap">{code}</span>
            ),
        },
        {
            title: t('common.name'),
            dataIndex: 'name',
            key: 'name',
            render: (name) => <span className="text-sm text-slate-700">{name}</span>,
        },
        {
            title: t('mkb10.additionalInfo'),
            dataIndex: 'additional_info',
            key: 'additional_info',
            render: (text) => <span className="text-xs text-slate-500">{shortenInfo(text)}</span>,
        },
    ];

    const renderMobileCard = (record) => (
        <div>
            <span className="block text-sm font-semibold text-[#1D2C7B] tabular-nums">{record.code}</span>
            <p className="mt-1 mb-0 text-sm text-slate-700">{record.name}</p>
            {record.additional_info && (
                <p className="mt-1 mb-0 text-xs text-slate-500">{shortenInfo(record.additional_info)}</p>
            )}
        </div>
    );

    return (
        <PageShell>
            <PageCard>
                <PageToolbar
                    title={t('mkb10.title')}
                    filters={
                        <Input
                            allowClear
                            size="small"
                            placeholder={t('common.search')}
                            prefix={<MagnifyingGlassIcon className="h-4 w-4 text-slate-400" />}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="md:!w-[260px]"
                        />
                    }
                    actions={
                        <ActionButton
                            onClick={handleSearch}
                            loading={isLoading}
                            icon={<MagnifyingGlassIcon className="h-4 w-4" />}
                        />
                    }
                />

                <DataTable
                    dataSource={records}
                    columns={columns}
                    loading={isLoading}
                    mobileCard={renderMobileCard}
                    scrollX={760}
                    pagination={false}
                />

                <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 px-3 py-2.5">
                    <span className="text-xs text-slate-500 tabular-nums">
                        {t('common.pageOf', { current: currentPage, total: totalPages })}
                    </span>
                    <Pagination
                        size="small"
                        current={currentPage}
                        total={totalPages * 10} // Assuming 10 items per page
                        onChange={handlePageChange}
                        pageSize={10} // Assuming 10 items per page
                    />
                </div>
            </PageCard>
        </PageShell>
    );
}
